/**
 * View-model helpers for the LP request detail page
 * (production-details.html #reqDetail / openRequest).
 */

import { formatLongDate, formatTaskDueLabel } from './orgLpHelpers';
import { daysUntil } from './lpProductionHelpers';

const TIMING_RANK = { 'At risk': 0, Urgent: 1, 'On track': 2 };

export function requestDisplayTitle(task) {
  const slug = String(task?.slug || '').toLowerCase();
  const title = String(task?.title || '').trim();
  if (slug === 'nda' || /\bnda\b/i.test(title)) return 'Production NDA';
  if (slug === 'itinerary' || /itinerary/i.test(title)) return 'Travel itinerary';
  if (slug === 'safety' || /safety/i.test(title)) return 'Safety briefing';
  return title.replace(/^Upload\s+/i, '').replace(/^Sign\s+/i, '') || 'Task';
}

/**
 * Derive timing label from schedule start (HTML: daysOut − lead time).
 * @param {{ schedule_start_date?: string|null, at_risk?: boolean }} person
 */
export function requestTiming(person) {
  if (person?.at_risk) return { key: 'bad', label: 'At risk', rank: 0 };
  const days = daysUntil(person?.schedule_start_date);
  if (days == null) return { key: 'ok', label: 'On track', rank: 2 };
  // Approximate visa lead runway vs first call.
  const runway = days - 14;
  if (runway <= 0) return { key: 'bad', label: 'At risk', rank: 0 };
  if (runway <= 7) return { key: 'warn', label: 'Urgent', rank: 1 };
  return { key: 'ok', label: 'On track', rank: 2 };
}

function personTaskStatus(outstandingRow) {
  if (!outstandingRow) {
    return { label: 'Complete', cls: 'complete' };
  }
  if (outstandingRow.message_opened) {
    return { label: 'In Process', cls: 'in-process' };
  }
  return { label: 'Outstanding', cls: 'outstanding' };
}

/**
 * @param {object} task - OrgProductionTaskOut
 * @param {'active'|'paused'|'done'} [state]
 * @param {Array<object>} [people] - production participants for full roster table
 */
export function buildRequestDetailView(task, state = 'active', people = []) {
  if (!task) return null;

  const total = Number(task.total_count) || 0;
  const done = Number(task.done_count) || 0;
  const left = Math.max(0, total - done);
  const series = Array.isArray(task.progress_series) ? task.progress_series : [];
  const activity = Array.isArray(task.activity) ? task.activity : [];
  const outstandingList = Array.isArray(task.outstanding) ? task.outstanding : [];
  const outstandingById = new Map(
    outstandingList.map((p) => [String(p.id), p]),
  );

  const items = outstandingList.map((p, i) => {
    const timing = requestTiming(p);
    return {
      id: p.id,
      name: p.name || '-',
      role: p.job_title || '-',
      location: p.location || 'Unassigned',
      email: p.email || '',
      scheduleStart: p.schedule_start_date || null,
      scheduleLabel: p.schedule_start_date ? formatTaskDueLabel(p.schedule_start_date) : '',
      daysLeft: daysUntil(p.schedule_start_date),
      timing,
      reached: p.message_opened != null ? Boolean(p.message_opened) : (i % 4) !== 0,
    };
  });

  const rosterSource = (people && people.length)
    ? people
    : outstandingList;

  const participants = rosterSource.map((p) => {
    const outstandingRow = outstandingById.get(String(p.id));
    const status = personTaskStatus(outstandingRow);
    const neededRaw = p.schedule_start_date || outstandingRow?.schedule_start_date;
    return {
      id: p.id,
      name: p.name || '-',
      role: p.job_title || p.department || '-',
      location: p.location || '—',
      neededBy: neededRaw ? formatLongDate(neededRaw) : '—',
      statusLabel: status.label,
      statusClass: status.cls,
    };
  }).sort((a, b) => {
    const order = { outstanding: 0, 'in-process': 1, complete: 2 };
    return (order[a.statusClass] - order[b.statusClass])
      || String(a.name).localeCompare(String(b.name));
  });

  const inProcessCount = participants.filter((p) => p.statusClass === 'in-process').length;
  const outstandingCount = participants.filter((p) => p.statusClass === 'outstanding').length;

  const atRisk = items.filter((it) => it.timing.label === 'At risk').length;
  const urgent = items.filter((it) => it.timing.label === 'Urgent').length;

  const effectiveState = task.is_complete ? 'done' : state;
  const statusLabel = effectiveState === 'done'
    ? 'Complete'
    : effectiveState === 'paused'
      ? 'Paused'
      : 'Active';

  return {
    slug: task.slug,
    title: requestDisplayTitle(task),
    unit: task.progress_verb || 'completed',
    outLabel: task.outstanding_label || 'Outstanding',
    total: total || participants.length,
    done,
    left,
    outstandingCount,
    inProcessCount,
    isComplete: Boolean(task.is_complete) || (total > 0 && done >= total),
    atRisk,
    urgent,
    status: effectiveState,
    statusLabel,
    series,
    activity,
    items,
    participants,
    attachment: task.attachment_label
      ? String(task.attachment_label).replace(/^Attached:\s*/i, '')
      : null,
    dueLabel: task.due_date ? formatTaskDueLabel(task.due_date) : '',
    dueLong: formatLongDate(task.due_date) || '',
    reminderLong: formatLongDate(task.last_reminder_date) || '',
  };
}

/**
 * Filter outstanding rows for the request detail list.
 * @param {ReturnType<typeof buildRequestDetailView>['items']} items
 * @param {'all'|'risk'|'urgent'|'unreached'} filter
 */
export function filterRequestItems(items, filter) {
  if (filter === 'risk') return items.filter((it) => it.timing.label === 'At risk');
  if (filter === 'urgent') return items.filter((it) => it.timing.label === 'Urgent');
  if (filter === 'unreached') return items.filter((it) => !it.reached);
  return items;
}

/**
 * Group outstanding people by location (HTML rd-group).
 * @param {ReturnType<typeof buildRequestDetailView>['items']} items
 */
export function groupRequestItemsByLocation(items) {
  const byLoc = {};
  items.forEach((it) => {
    const key = it.location || 'Unassigned';
    (byLoc[key] = byLoc[key] || []).push(it);
  });
  return Object.keys(byLoc)
    .sort((a, b) => a.localeCompare(b))
    .map((loc) => ({
      location: loc,
      scheduleLabel: byLoc[loc].find((p) => p.scheduleLabel)?.scheduleLabel || '',
      daysLeft: byLoc[loc].find((p) => p.daysLeft != null)?.daysLeft,
      people: byLoc[loc].slice().sort(
        (a, b) => (TIMING_RANK[a.timing.label] - TIMING_RANK[b.timing.label])
          || a.name.localeCompare(b.name),
      ),
    }));
}

/**
 * SVG chart for remaining people over time (HTML chartSVG).
 * @param {{ total: number, series: Array<{ d: string, r: number, rem?: boolean }> }} view
 */
export function requestProgressChartSvg(view) {
  const series = view?.series || [];
  const maxY = Math.max(1, view?.total || 0);
  if (!series.length) return null;

  const W = 780;
  const H = 250;
  const pl = 34;
  const pr = 16;
  const pt = 16;
  const pb = 30;
  const iw = W - pl - pr;
  const ih = H - pt - pb;
  const n = series.length;
  const x = (i) => pl + (n === 1 ? 0 : (iw * i) / (n - 1));
  const y = (v) => pt + ih * (1 - v / maxY);
  const pts = series.map((p, i) => [x(i), y(p.r)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L ${x(n - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;

  const gridVals = [0, Math.round(maxY / 2), maxY];
  const grid = gridVals.map((v) => (
    `<line x1="${pl}" y1="${y(v).toFixed(1)}" x2="${W - pr}" y2="${y(v).toFixed(1)}" stroke="#EAECEF"/>`
    + `<text x="${pl - 8}" y="${(y(v) + 4).toFixed(1)}" text-anchor="end" font-size="10" fill="#93A0AB">${v}</text>`
  )).join('');

  const marks = series.map((p, i) => (
    p.rem
      ? `<line x1="${x(i).toFixed(1)}" y1="${pt}" x2="${x(i).toFixed(1)}" y2="${pt + ih}" stroke="#B07A1E" stroke-width="1" stroke-dasharray="3 3"/>`
        + `<circle cx="${x(i).toFixed(1)}" cy="${pt}" r="3" fill="#B07A1E"/>`
      : ''
  )).join('');

  const xl = series.map((p, i) => (
    (i % 2 === 0 || i === n - 1)
      ? `<text x="${x(i).toFixed(1)}" y="${H - 9}" text-anchor="middle" font-size="10" fill="#93A0AB">${p.d}</text>`
      : ''
  )).join('');

  const dots = pts.map((p) => (
    `<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="2.5" fill="#1F6FB2"/>`
  )).join('');

  return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="max-width:${W}px" aria-hidden="true">${grid}${marks}<path d="${area}" fill="rgba(31,111,178,0.10)"/><path d="${line}" fill="none" stroke="#1F6FB2" stroke-width="2"/>${dots}${xl}</svg>`;
}
